import { useState, useEffect, useRef } from 'react';
import { Send, Trash2, Loader2, Dumbbell, Apple, Heart, Zap, Brain, Bot, WifiOff } from 'lucide-react';
import marcoAvatar from '@/assets/marco-avatar-circle.png';
import { SectionHeader } from './SectionHeader';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast as sonnerToast } from 'sonner';
import { toastError } from '@/lib/toastError';
import { isOnline, cachedQuery, setCachedData, getCachedData } from '@/lib/offlineStorage';
import { getFunctionUrlWithApiKey } from '@/lib/edgeFunctionUrl';

interface MarcoAssistantSectionProps {
  activeUser: string;
  viewingUserId?: string | null;
}

interface ChatMessage {
  id?: number;
  role: 'user' | 'assistant';
  content: string;
}

// Helper defensivo: no crashea module-load si las env vars faltan.
const CHAT_URL = getFunctionUrlWithApiKey('chat-marco');

export function MarcoAssistantSection({ activeUser, viewingUserId }: MarcoAssistantSectionProps) {
  const { user } = useAuth();
  const isCoachView = Boolean(viewingUserId);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [online, setOnline] = useState(navigator.onLine);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const scrollToBottom = () => {
    if (!messagesContainerRef.current) return;
    messagesContainerRef.current.scrollTo({
      top: messagesContainerRef.current.scrollHeight,
      behavior: 'smooth',
    });
  };

  useEffect(() => { scrollToBottom(); }, [messages]);

  // Track online status for this component
  useEffect(() => {
    const onOnline = () => setOnline(true);
    const onOffline = () => setOnline(false);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);

  // Load chat history (with offline cache)
  useEffect(() => {
    if (!user) return;
    setLoadingHistory(true);

    const targetUserId = viewingUserId || user.id;
    const cacheKey = `chat_${targetUserId}`;

    cachedQuery<any[]>(cacheKey, () => {
      let q = supabase.from('chat_messages').select('*').eq('user_id', targetUserId).order('created_at', { ascending: true });
      if (activeUser) q = q.eq('profile_name', activeUser);
      return q;
    }).then(({ data }) => {
      if (data) {
        const msgs = data.map((m: any) => ({ id: m.id, role: m.role, content: m.content }));
        setMessages(msgs);
      }
      setLoadingHistory(false);
    });
  }, [user, activeUser]);

  const persistMessage = async (role: 'user' | 'assistant', content: string) => {
    if (!user) return;
    await supabase.from('chat_messages').insert({
      user_id: user.id,
      profile_name: activeUser,
      role,
      content,
    });
  };

  const clearHistory = async () => {
    if (!user) return;
    await supabase.from('chat_messages').delete()
      .eq('user_id', user.id)
      .eq('profile_name', activeUser);
    setMessages([]);
    sonnerToast.success('Historial de Marco eliminado');
  };

  const sendMessage = async () => {
    if (!input.trim() || isStreaming || !user) return;
    if (!isOnline()) {
      sonnerToast.error('Marco requiere conexión a internet. Tus demás datos están guardados.');
      return;
    }

    const userMsg: ChatMessage = { role: 'user', content: input.trim() };
    const updatedMessages = [...messages, userMsg];
    setMessages(updatedMessages);
    setInput('');
    setIsStreaming(true);

    await persistMessage('user', userMsg.content);

    let assistantContent = '';

    try {
      const session = await supabase.auth.getSession();
      const token = session.data.session?.access_token;

      const resp = await fetch(CHAT_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          messages: updatedMessages.map(m => ({ role: m.role, content: m.content })),
        }),
      });

      if (!resp.ok) {
        let errMsg = 'Error al conectar con Marco';
        try {
          const errData = await resp.json();
          if (errData?.error) errMsg = errData.error;
        } catch {}
        sonnerToast.error(errMsg);
        setIsStreaming(false);
        return;
      }

      if (!resp.body) {
        sonnerToast.error('No se recibió respuesta');
        setIsStreaming(false);
        return;
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let textBuffer = '';
      let streamDone = false;

      const updateAssistant = (text: string) => {
        setMessages(prev => {
          const last = prev[prev.length - 1];
          if (last?.role === 'assistant') {
            return prev.map((m, i) => i === prev.length - 1 ? { ...m, content: text } : m);
          }
          return [...prev, { role: 'assistant', content: text }];
        });
      };

      while (!streamDone) {
        const { done, value } = await reader.read();
        if (done) break;
        textBuffer += decoder.decode(value, { stream: true });

        let newlineIndex: number;
        while ((newlineIndex = textBuffer.indexOf('\n')) !== -1) {
          let line = textBuffer.slice(0, newlineIndex);
          textBuffer = textBuffer.slice(newlineIndex + 1);

          if (line.endsWith('\r')) line = line.slice(0, -1);
          if (line.startsWith(':') || line.trim() === '') continue;
          if (!line.startsWith('data: ')) continue;

          const jsonStr = line.slice(6).trim();
          if (jsonStr === '[DONE]') {
            streamDone = true;
            break;
          }

          try {
            const parsed = JSON.parse(jsonStr);
            const content = parsed.choices?.[0]?.delta?.content as string | undefined;
            if (content) {
              assistantContent += content;
              updateAssistant(assistantContent);
            }
          } catch {
            textBuffer = line + '\n' + textBuffer;
            break;
          }
        }
      }

      // Final flush
      if (textBuffer.trim()) {
        for (let raw of textBuffer.split('\n')) {
          if (!raw) continue;
          if (raw.endsWith('\r')) raw = raw.slice(0, -1);
          if (raw.startsWith(':') || raw.trim() === '') continue;
          if (!raw.startsWith('data: ')) continue;
          const jsonStr = raw.slice(6).trim();
          if (jsonStr === '[DONE]') continue;
          try {
            const parsed = JSON.parse(jsonStr);
            const content = parsed.choices?.[0]?.delta?.content as string | undefined;
            if (content) {
              assistantContent += content;
              updateAssistant(assistantContent);
            }
          } catch { /* ignore */ }
        }
      }

      if (assistantContent) {
        await persistMessage('assistant', assistantContent);
      }
    } catch (err: any) {
      console.error('Stream error:', err);
      toastError('Error de conexión con Marco', {
        section: 'marco-ia-stream',
        extra: { errorMessage: err?.message, stack: err?.stack },
      });
    } finally {
      setIsStreaming(false);
    }
  };

  if (!user) {
    return (
      <section id="marco" className="scroll-mt-32">
        <SectionHeader title="Marco" subtitle="Tu Asistente IA" icon={Bot} helpId="marco" />
        <p className="text-muted-foreground text-center py-12 font-bold uppercase tracking-widest text-sm">
          Inicia sesión para hablar con Marco
        </p>
      </section>
    );
  }

  const quickQuestions = [
    { icon: Dumbbell, text: '¿Cómo mejorar mi press de banca?', color: 'text-red-400' },
    { icon: Apple, text: '¿Cuánta proteína debo consumir?', color: 'text-green-400' },
    { icon: Heart, text: '¿Cómo mejorar mi recuperación?', color: 'text-pink-400' },
    { icon: Brain, text: '¿Qué suplementos recomiendas?', color: 'text-blue-400' },
  ];

  return (
    <section id="marco" className="scroll-mt-32">
      <SectionHeader title="Marco" subtitle="Tu Asistente IA" icon={Bot} helpId="marco" />

      <div className="relative bg-card border-4 border-border rounded-2xl overflow-hidden shadow-[0_0_60px_-15px_hsl(var(--accent)/0.15)] flex flex-col" style={{ height: '650px' }}>
        {/* Decorative background gradient */}
        <div className="absolute inset-0 pointer-events-none opacity-[0.03]">
          <div className="absolute top-0 right-0 w-96 h-96 bg-accent rounded-full blur-[120px]" />
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-accent rounded-full blur-[100px]" />
        </div>

        {/* Header */}
        <div className="relative flex items-center justify-between px-4 md:px-6 py-3 bg-primary border-b-2 border-accent/30">
          <div className="flex items-center gap-3">
            <div className="relative">
              <div className="w-10 h-10 rounded-full overflow-hidden shadow-lg shadow-accent/30">
                <img src={marcoAvatar} alt="Marco" className="w-full h-full object-cover" />
              </div>
              <div className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-primary ${isStreaming ? 'bg-yellow-400 animate-pulse' : 'bg-green-400'}`} />
            </div>
            <div>
              <div className="text-sm font-black uppercase tracking-widest text-primary-foreground flex items-center gap-2">
                Marco
                <Zap className="w-3.5 h-3.5 text-accent" />
              </div>
              <div className="text-[10px] text-primary-foreground/50 uppercase tracking-wider">
                {isStreaming ? '⚡ Escribiendo...' : 'En línea · Fitness & Nutrición'}
              </div>
            </div>
          </div>
          {messages.length > 0 && !isCoachView && (
            <button
              onClick={clearHistory}
              className="text-primary-foreground/30 hover:text-destructive transition-all duration-200 p-2 rounded-lg hover:bg-destructive/10"
              title="Limpiar historial"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Messages */}
        <div ref={messagesContainerRef} className="relative flex-1 overflow-y-auto p-4 md:p-6 space-y-4">
          {loadingHistory && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-accent" />
            </div>
          )}

          {!loadingHistory && messages.length === 0 && (
            <div className="flex flex-col items-center justify-center h-full text-center space-y-6 py-4">
              {/* Avatar grande animado */}
              <div className="relative">
                <div className="w-20 h-20 rounded-full overflow-hidden shadow-2xl shadow-accent/30 animate-pulse">
                  <img src={marcoAvatar} alt="Marco" className="w-full h-full object-cover" />
                </div>
                <div className="absolute -top-1 -right-1 w-6 h-6 bg-green-400 rounded-full border-4 border-card flex items-center justify-center">
                  <Zap className="w-3 h-3 text-green-900" />
                </div>
              </div>

              <div className="space-y-2">
                <h4 className="text-xl md:text-2xl font-black uppercase tracking-wider">
                  ¡Hola! Soy <span className="text-accent">Marco</span> 💪
                </h4>
                <p className="text-muted-foreground text-xs md:text-sm max-w-md leading-relaxed">
                  Tu asistente de inteligencia artificial especializado en entrenamiento, nutrición y bienestar. ¡Pregúntame lo que quieras!
                </p>
              </div>

              {/* Quick question cards */}
              <div className="grid grid-cols-2 gap-2 md:gap-3 w-full max-w-md">
                {quickQuestions.map((q) => (
                  <button
                    key={q.text}
                    onClick={() => setInput(q.text)}
                    className="group flex items-start gap-2.5 p-3 md:p-4 rounded-xl bg-secondary/80 border-2 border-border hover:border-accent/50 hover:bg-accent/5 transition-all duration-200 text-left"
                  >
                    <q.icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${q.color} group-hover:scale-110 transition-transform`} />
                    <span className="text-[10px] md:text-xs font-bold text-muted-foreground group-hover:text-foreground transition-colors leading-tight">
                      {q.text}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((msg, i) => (
            <div
              key={i}
              className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : 'justify-start'} animate-in fade-in slide-in-from-bottom-2 duration-300`}
            >
              {msg.role === 'assistant' && (
                <div className="w-7 h-7 rounded-full overflow-hidden flex-shrink-0 mt-1 shadow-md shadow-accent/20">
                  <img src={marcoAvatar} alt="Marco" className="w-full h-full object-cover" />
                </div>
              )}
              <div
                className={`max-w-[80%] rounded-2xl px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${
                  msg.role === 'user'
                    ? 'bg-accent text-accent-foreground rounded-br-md shadow-lg shadow-accent/20'
                    : 'bg-secondary/80 text-foreground rounded-bl-md border border-border/50'
                }`}
              >
                {msg.content}
              </div>
              {msg.role === 'user' && (
                <div className="w-7 h-7 rounded-full bg-primary flex-shrink-0 flex items-center justify-center mt-1 border-2 border-border">
                  <span className="text-[10px] font-black text-primary-foreground">TÚ</span>
                </div>
              )}
            </div>
          ))}

          {isStreaming && messages[messages.length - 1]?.role !== 'assistant' && (
            <div className="flex gap-3 justify-start animate-in fade-in duration-300">
              <div className="w-7 h-7 rounded-full overflow-hidden flex-shrink-0 mt-1 shadow-md shadow-accent/20">
                <img src={marcoAvatar} alt="Marco" className="w-full h-full object-cover" />
              </div>
              <div className="bg-secondary/80 rounded-2xl rounded-bl-md px-4 py-3 border border-border/50">
                <div className="flex gap-1.5">
                  <span className="w-2 h-2 bg-accent/60 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <span className="w-2 h-2 bg-accent/60 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <span className="w-2 h-2 bg-accent/60 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              </div>
            </div>
          )}

          
        </div>

        {/* Input */}
        <div className="relative border-t-2 border-border p-3 md:p-4 bg-primary/5 backdrop-blur-sm">
          {!online && (
            <div className="flex items-center gap-2 mb-2 px-2 py-1.5 rounded-lg bg-destructive/10 text-destructive text-xs font-bold">
              <WifiOff className="w-3.5 h-3.5" />
              <span>Marco necesita internet para responder. Puedes releer conversaciones anteriores mientras tanto.</span>
            </div>
          )}
          <div className="flex gap-2 items-end">
            <textarea
              ref={inputRef}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  sendMessage();
                }
              }}
              placeholder={isCoachView ? "Solo lectura — vista del coach" : online ? "Pregúntale algo a Marco..." : "Sin conexión — solo lectura"}
              className="fitness-input flex-1 resize-none min-h-[44px] max-h-[120px] !bg-secondary/80 !border-border/50 focus:!border-accent/50"
              rows={1}
              disabled={isStreaming || !online || isCoachView}
            />
            <button
              onClick={sendMessage}
              disabled={isStreaming || !input.trim() || !online || isCoachView}
              className="relative w-11 h-11 min-w-[44px] min-h-[44px] rounded-xl bg-gradient-to-br from-accent to-accent/80 text-accent-foreground flex items-center justify-center shadow-lg shadow-accent/30 hover:shadow-accent/50 disabled:opacity-30 disabled:shadow-none transition-all duration-200 hover:scale-105 active:scale-95"
            >
              {isStreaming ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
